import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { API_BASE_URL } from '../../utils/apiConfig';
import { FILTER_DEFAULTS } from '../../hooks/useFilters';
import './FilterDropdown.css';

// Exponential scale constants
const WHALE_MIN = 10;          // 10K
const WHALE_MAX = 25000;       // 25M
const WHALE_SLIDER_MAX = 1000;

const LIQ_MIN = 10;            // 10K
const LIQ_MAX = 10000;         // 10M
const LIQ_SLIDER_MAX = 1000;

// Conversion functions for Whale Trades (exponential scale)
const sliderToWhaleValue = (position) => {
  const ratio = WHALE_MAX / WHALE_MIN;
  const valueInK = WHALE_MIN * Math.pow(ratio, position / WHALE_SLIDER_MAX);
  const rounded = Math.round(valueInK / 10) * 10; // Round to nearest 10K
  return rounded * 1000; // Convert to dollars
};

const whaleValueToSlider = (valueInDollars) => {
  const valueInK = valueInDollars / 1000;
  const ratio = WHALE_MAX / WHALE_MIN;
  return Math.log(valueInK / WHALE_MIN) / Math.log(ratio) * WHALE_SLIDER_MAX;
};

// Conversion functions for Liquidations (exponential scale)
const sliderToLiqValue = (position) => {
  const ratio = LIQ_MAX / LIQ_MIN;
  const valueInK = LIQ_MIN * Math.pow(ratio, position / LIQ_SLIDER_MAX);
  const rounded = Math.round(valueInK / 10) * 10; // Round to nearest 10K
  return rounded * 1000; // Convert to dollars
};

const liqValueToSlider = (valueInDollars) => {
  const valueInK = valueInDollars / 1000;
  const ratio = LIQ_MAX / LIQ_MIN;
  return Math.log(valueInK / LIQ_MIN) / Math.log(ratio) * LIQ_SLIDER_MAX;
};

const ASSET_CATEGORIES = [
  { value: 'crypto', label: 'Crypto' },
  { value: 'stock', label: 'Stocks' },
  { value: 'commodity', label: 'Commodities' },
  { value: 'index', label: 'Indices' },
  { value: 'forex', label: 'Forex' },
];

function FilterDropdown({ filters, setWhaleTradesMinVolume, setLiquidationsMinVolume, setSelectedCoins, toggleCategory, clearCategories }) {
  const { t } = useTranslation();

  // Local state for input values (in dollars)
  const [whaleValue, setWhaleValue] = useState(filters.whaleTradesMinVolume);
  const [liqValue, setLiqValue] = useState(filters.liquidationsMinVolume);

  // Slider positions (0-1000)
  const [whaleSliderPos, setWhaleSliderPos] = useState(() => whaleValueToSlider(filters.whaleTradesMinVolume));
  const [liqSliderPos, setLiqSliderPos] = useState(() => liqValueToSlider(filters.liquidationsMinVolume));

  const [coinInput, setCoinInput] = useState('');
  const [selectedCoins, setSelectedCoinsLocal] = useState(filters.selectedCoins || []);
  const [isWhaleEditing, setIsWhaleEditing] = useState(false);
  const [isLiqEditing, setIsLiqEditing] = useState(false);
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [indicatorsLoaded, setIndicatorsLoaded] = useState(false);

  const whaleInputRef = useRef(null);
  const liqInputRef = useRef(null);
  const dropdownRef = useRef(null);

  // Sync local state with filters prop
  useEffect(() => {
    setWhaleValue(filters.whaleTradesMinVolume);
    setLiqValue(filters.liquidationsMinVolume);
    setWhaleSliderPos(whaleValueToSlider(filters.whaleTradesMinVolume));
    setLiqSliderPos(liqValueToSlider(filters.liquidationsMinVolume));
    setSelectedCoinsLocal(filters.selectedCoins || []);
  }, [filters.whaleTradesMinVolume, filters.liquidationsMinVolume, filters.selectedCoins]);

  // Load available coins from mapped_symbols.json
  useEffect(() => {
    const loadCoins = async () => {
      try {
        const response = await fetch(`${API_BASE_URL}/mapped_symbols.json`);
        if (response.ok) {
          const data = await response.json();
          const coinList = Object.keys(data).map(symbol =>
            symbol.replace(/USDT$|USD$/, '')
          );
          setSuggestions([...new Set(coinList)].sort());
        }
      } catch (error) {
        console.error('Error loading coins:', error);
      }
    };
    loadCoins();
  }, []);

  // Format value for display
  const formatValue = (value) => {
    if (value >= 1e6) return (value / 1e6).toFixed(1) + 'M';
    if (value >= 1e3) return (value / 1e3).toFixed(0) + 'K';
    return value.toString();
  };

  // Parse display value to number
  const parseValue = (str) => {
    const trimmed = str.trim().toUpperCase().replace(/[$,]/g, '');
    if (trimmed.endsWith('M')) {
      return parseFloat(trimmed) * 1e6;
    } else if (trimmed.endsWith('K')) {
      return parseFloat(trimmed) * 1e3;
    }
    return parseFloat(trimmed) || 0;
  };

  // Handle whale trade editing
  const handleWhaleEditClick = () => {
    setIsWhaleEditing(true);
    setTimeout(() => whaleInputRef.current?.focus(), 0);
  };

  const handleWhaleConfirm = () => {
    const parsed = parseValue(whaleInputRef.current.value);
    const clamped = Math.max(10000, Math.min(25000000, parsed));
    setWhaleValue(clamped);
    setWhaleSliderPos(whaleValueToSlider(clamped));
    setIsWhaleEditing(false);
  };

  const handleWhaleKeyDown = (e) => {
    if (e.key === 'Enter') {
      handleWhaleConfirm();
    } else if (e.key === 'Escape') {
      setIsWhaleEditing(false);
    }
  };

  // Handle liquidation editing
  const handleLiqEditClick = () => {
    setIsLiqEditing(true);
    setTimeout(() => liqInputRef.current?.focus(), 0);
  };

  const handleLiqConfirm = () => {
    const parsed = parseValue(liqInputRef.current.value);
    const clamped = Math.max(10000, Math.min(10000000, parsed));
    setLiqValue(clamped);
    setLiqSliderPos(liqValueToSlider(clamped));
    setIsLiqEditing(false);
  };

  const handleLiqKeyDown = (e) => {
    if (e.key === 'Enter') {
      handleLiqConfirm();
    } else if (e.key === 'Escape') {
      setIsLiqEditing(false);
    }
  };

  // Handle coin input change
  const handleCoinInputChange = (e) => {
    const value = e.target.value.toUpperCase();
    setCoinInput(value);
    setShowSuggestions(value.length > 0);
  };

  // Handle suggestion click - add coin to selected list
  const handleSuggestionClick = (coin) => {
    if (!selectedCoins.includes(coin)) {
      setSelectedCoinsLocal([...selectedCoins, coin]);
    }
    setCoinInput('');
    setShowSuggestions(false);
  };

  // Handle remove coin from selected list
  const handleRemoveCoin = (coinToRemove) => {
    setSelectedCoinsLocal(selectedCoins.filter(c => c !== coinToRemove));
  };

  // Handle Enter key in input
  const handleCoinInputKeyDown = (e) => {
    if (e.key === 'Enter' && coinInput.trim()) {
      const coin = coinInput.trim().toUpperCase();
      if (!selectedCoins.includes(coin)) {
        setSelectedCoinsLocal([...selectedCoins, coin]);
      }
      setCoinInput('');
      setShowSuggestions(false);
    }
  };

  // Apply filters
  const handleApply = () => {
    setWhaleTradesMinVolume(whaleValue);
    setLiquidationsMinVolume(liqValue);
    setSelectedCoins(selectedCoins);

    console.log('🎯 Filters applied:', {
      whaleTradesMinVolume: whaleValue,
      liquidationsMinVolume: liqValue,
      selectedCoins: selectedCoins
    });

    // Close dropdown
    const dropdown = dropdownRef.current?.closest('.dropdown');
    if (dropdown) {
      const bsDropdown = window.bootstrap?.Dropdown?.getInstance(dropdown.querySelector('[data-bs-toggle="dropdown"]'));
      bsDropdown?.hide();
    }
  };

  // Reset to defaults
  const handleDefault = () => {
    const defaultWhale = FILTER_DEFAULTS.whaleTradesMinVolume;
    const defaultLiq = FILTER_DEFAULTS.liquidationsMinVolume;

    setWhaleValue(defaultWhale);
    setLiqValue(defaultLiq);
    setSelectedCoinsLocal([]);

    setWhaleSliderPos(whaleValueToSlider(defaultWhale));
    setLiqSliderPos(liqValueToSlider(defaultLiq));

    setWhaleTradesMinVolume(defaultWhale);
    setLiquidationsMinVolume(defaultLiq);
    setSelectedCoins([]);
    if (clearCategories) clearCategories();

    console.log('🔄 Filters reset to defaults');

    // Close dropdown
    const dropdown = dropdownRef.current?.closest('.dropdown');
    if (dropdown) {
      const bsDropdown = window.bootstrap?.Dropdown?.getInstance(dropdown.querySelector('[data-bs-toggle="dropdown"]'));
      bsDropdown?.hide();
    }
  };

  // Filter suggestions (exclude already selected coins)
  const filteredSuggestions = suggestions.filter(coin =>
    !selectedCoins.includes(coin) && (coin.includes(coinInput) || coin.startsWith(coinInput))
  ).slice(0, 10);

  // Check if filters are active (not default)
  const hasActiveFilters = whaleValue !== FILTER_DEFAULTS.whaleTradesMinVolume ||
    liqValue !== FILTER_DEFAULTS.liquidationsMinVolume ||
    selectedCoins.length > 0 ||
    (filters.selectedCategories && filters.selectedCategories.length > 0);

  // Check if local state differs from applied global filters
  const hasUnsavedChanges = useMemo(() => {
    return whaleValue !== filters.whaleTradesMinVolume ||
      liqValue !== filters.liquidationsMinVolume ||
      JSON.stringify(selectedCoins) !== JSON.stringify(filters.selectedCoins || []);
  }, [whaleValue, liqValue, selectedCoins, filters]);

  // Mark indicators as loaded after first appearance (to prevent animation on every change)
  useEffect(() => {
    if (hasActiveFilters && !indicatorsLoaded) {
      setTimeout(() => {
        setIndicatorsLoaded(true);
      }, 500); // After initial animation completes
    }
  }, [hasActiveFilters, indicatorsLoaded]);

  return (
    <div className="dropdown filter-dropdown" ref={dropdownRef}>
      <button
        className={`filter dropdown-toggle ${hasActiveFilters ? 'active' : ''} ${indicatorsLoaded ? 'indicators-loaded' : ''}`}
        type="button"
        data-bs-toggle="dropdown"
        data-bs-offset="0,8"
        aria-expanded="false"
      >
        {/* Filter Icon */}
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" width="16" height="16">
          <path d="M10 18h4v-2h-4v2zM3 6v2h18V6H3zm3 7h12v-2H6v2z"/>
        </svg>

        {/* Active Filter Indicators */}
        {hasActiveFilters && (
          <span className="filter-indicators">
            <span className="filter-values-container">
              <span className="filter-value">{formatValue(whaleValue)}</span>
              <span className="filter-separator">/</span>
              <span className="filter-value">{formatValue(liqValue)}</span>
            </span>
            {selectedCoins.length > 0 && (
              <span className="filter-coins">+{selectedCoins.length}</span>
            )}
          </span>
        )}
      </button>
      <div className="dropdown-menu" onClick={(e) => e.stopPropagation()}>
        {/* Whale Trades Filter */}
        <div className="filter-item">
          <div className="filter-header">
            <label className="col-form-label">{t('filters.whaleTradesMin')}</label>
            <div className="value-edit-container">
              {!isWhaleEditing ? (
                <>
                  <span className="value-display">${formatValue(whaleValue)}</span>
                  <span className="edit-icon" onClick={handleWhaleEditClick} title="Edit">✏️</span>
                </>
              ) : (
                <>
                  <input
                    ref={whaleInputRef}
                    type="text"
                    className="value-input"
                    defaultValue={formatValue(whaleValue)}
                    onKeyDown={handleWhaleKeyDown}
                    onBlur={handleWhaleConfirm}
                  />
                  <span className="confirm-icon" onClick={handleWhaleConfirm} title="Confirm">✓</span>
                </>
              )}
            </div>
          </div>
          <div className="slider-container">
            <input
              type="range"
              className="form-range"
              min="0"
              max={WHALE_SLIDER_MAX}
              step="1"
              value={whaleSliderPos}
              onChange={(e) => {
                const position = Number(e.target.value);
                setWhaleSliderPos(position);
                const dollarValue = sliderToWhaleValue(position);
                setWhaleValue(dollarValue);
              }}
            />
          </div>
        </div>

        {/* Liquidations Filter */}
        <div className="filter-item">
          <div className="filter-header">
            <label className="col-form-label">{t('filters.liquidationsMin')}</label>
            <div className="value-edit-container">
              {!isLiqEditing ? (
                <>
                  <span className="value-display">${formatValue(liqValue)}</span>
                  <span className="edit-icon" onClick={handleLiqEditClick} title="Edit">✏️</span>
                </>
              ) : (
                <>
                  <input
                    ref={liqInputRef}
                    type="text"
                    className="value-input"
                    defaultValue={formatValue(liqValue)}
                    onKeyDown={handleLiqKeyDown}
                    onBlur={handleLiqConfirm}
                  />
                  <span className="confirm-icon" onClick={handleLiqConfirm} title="Confirm">✓</span>
                </>
              )}
            </div>
          </div>
          <div className="slider-container">
            <input
              type="range"
              className="form-range"
              min="0"
              max={LIQ_SLIDER_MAX}
              step="1"
              value={liqSliderPos}
              onChange={(e) => {
                const position = Number(e.target.value);
                setLiqSliderPos(position);
                const dollarValue = sliderToLiqValue(position);
                setLiqValue(dollarValue);
              }}
            />
          </div>
        </div>

        {/* Coin Symbol Filter */}
        <div className="filter-item">
          <div className="filter-header">
            <label className="col-form-label">Symbols</label>
            <div className="autocomplete-container">
              <input
                type="text"
                className="form-control form-control-sm"
                placeholder="BTC, ETH..."
                value={coinInput}
                onChange={handleCoinInputChange}
                onKeyDown={handleCoinInputKeyDown}
                autoComplete="off"
              />
              {showSuggestions && filteredSuggestions.length > 0 && (
                <div className="autocomplete-suggestions">
                  {filteredSuggestions.map(coin => (
                    <div
                      key={coin}
                      className="autocomplete-suggestion"
                      onClick={() => handleSuggestionClick(coin)}
                    >
                      {coin}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {selectedCoins.length > 0 && (
            <div className="selected-coins-container">
              {selectedCoins.map(coin => (
                <div key={coin} className="coin-chip">
                  <span className="coin-chip-text">{coin}</span>
                  <button
                    className="coin-chip-remove"
                    onClick={() => handleRemoveCoin(coin)}
                    type="button"
                  >
                    ×
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Asset Category Filter (multi-select) */}
        {toggleCategory && (
          <div className="filter-item">
            <div className="filter-header">
              <label className="col-form-label">Asset Type</label>
            </div>
            <div className="category-pills">
              <button
                type="button"
                className={`category-pill ${(!filters.selectedCategories || filters.selectedCategories.length === 0) ? 'active' : ''}`}
                onClick={() => clearCategories && clearCategories()}
              >
                All
              </button>
              {ASSET_CATEGORIES.map(cat => (
                <button
                  key={cat.value}
                  type="button"
                  className={`category-pill ${(filters.selectedCategories || []).includes(cat.value) ? 'active' : ''}`}
                  onClick={() => toggleCategory(cat.value)}
                >
                  {cat.label}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Buttons */}
        <div className="filter-item">
          <div className="filter-buttons">
            <button type="button" className="btn btn-secondary btn-sm" onClick={handleDefault}>
              {t('filters.reset')}
            </button>
            <button type="button" className={`btn btn-primary btn-sm${hasUnsavedChanges ? ' unsaved-changes' : ''}`} onClick={handleApply}>
              {t('filters.apply')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default FilterDropdown;
